import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, KeyRound, ShieldCheck, CreditCard, CheckCircle2, Bot, Globe, Instagram, Package, Mail, AlertTriangle, Activity } from "lucide-react";

export default function SettingsPage() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const isGoogle = !user?.username && !!user?.picture;

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password");
    } finally { setSaving(false); }
  };

  return (
    <div className="animate-fade-up max-w-xl pb-16" data-testid="settings-page">
      <span className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-500">Account</span>
      <h1 className="font-heading font-light text-4xl tracking-tight mt-1 mb-2">Settings &amp; Security</h1>
      <p className="text-zinc-500 font-body mb-6">Manage your admin password, payments and bot-protection.</p>

      <div className="bg-white border border-zinc-200/80 rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-5 pb-5 border-b border-zinc-100">
          <div className="h-10 w-10 rounded-full bg-black text-white flex items-center justify-center">
            <KeyRound size={18} />
          </div>
          <div>
            <div className="font-heading font-semibold">Change password</div>
            <div className="text-xs text-zinc-500">{user?.email}</div>
          </div>
        </div>

        {isGoogle ? (
          <div className="flex items-start gap-3 text-sm text-zinc-600 bg-zinc-50 rounded-xl p-4" data-testid="settings-google-notice">
            <ShieldCheck size={18} className="text-emerald-600 mt-0.5" />
            You're signed in with Google. Password changes aren't needed for this account.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Current password</label>
              <input data-testid="current-password-input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-black bg-white" />
            </div>
            <div>
              <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">New password</label>
              <input data-testid="new-password-input" type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" placeholder="At least 8 characters" className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-black bg-white" />
            </div>
            <div>
              <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Confirm new password</label>
              <input data-testid="confirm-password-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-black bg-white" />
            </div>
            <button type="submit" data-testid="save-password-button" disabled={saving} className="w-full flex items-center justify-center gap-2 casper-btn rounded-full py-3.5 font-body font-semibold disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />} Update password
            </button>
          </form>
        )}
      </div>

      <SecurityAlerts />
      <AnalyticsSettings />
      <StripeSettings />
      <CaptchaSettings />
      <GeoSettings />
      <InstagramSettings />
      <UpsSettings />
      <MailgunSettings />
    </div>
  );
}

function StripeSettings() {
  const [status, setStatus] = useState(null);
  const [secret, setSecret] = useState("");
  const [webhook, setWebhook] = useState("");
  const [publishable, setPublishable] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = () => api.get("/admin/stripe-settings").then((r) => setStatus(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!secret.trim()) { toast.error("Enter your Stripe secret key"); return; }
    setSaving(true);
    try {
      await api.put("/admin/stripe-settings", { secret_key: secret.trim(), webhook_secret: webhook.trim(), publishable_key: publishable.trim() });
      toast.success("Stripe account connected");
      setSecret(""); setWebhook(""); setPublishable("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save Stripe keys"); }
    finally { setSaving(false); }
  };

  const disconnect = async () => {
    setClearing(true);
    try {
      await api.put("/admin/stripe-settings", { secret_key: "" });
      toast.success("Reverted to the platform Stripe account");
      await load();
    } catch { toast.error("Could not disconnect"); }
    finally { setClearing(false); }
  };

  return (
    <div className="bg-white border border-zinc-200/80 rounded-2xl p-6" data-testid="stripe-settings">
      <div className="flex items-center gap-3 mb-5 pb-5 border-b border-zinc-100">
        <div className="h-10 w-10 rounded-full bg-[#635bff] text-white flex items-center justify-center">
          <CreditCard size={18} />
        </div>
        <div className="flex-1">
          <div className="font-heading font-semibold">Payments — bring your own Stripe</div>
          <div className="text-xs text-zinc-500">Use your own Stripe account for all checkouts and subscriptions.</div>
        </div>
      </div>

      {status?.configured ? (
        <div className="flex items-center justify-between bg-emerald-50 border border-emerald-100 rounded-xl p-4 mb-4" data-testid="stripe-connected">
          <div className="flex items-center gap-2 text-sm text-emerald-800">
            <CheckCircle2 size={18} className="text-emerald-600" />
            Connected · <span className="font-mono">{status.mode || "?"}</span> key ••••{status.secret_last4} · webhook {status.webhook_set ? "set" : "not set"}
          </div>
          <button onClick={disconnect} disabled={clearing} data-testid="stripe-disconnect" className="text-xs font-semibold text-rose-600 hover:underline disabled:opacity-60">
            {clearing ? "…" : "Disconnect"}
          </button>
        </div>
      ) : (
        <p className="text-sm text-zinc-500 mb-4">Currently using the platform's built-in Stripe account. Connect your own below.</p>
      )}

      <div className="space-y-3">
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Secret key (sk_live_… or sk_test_…)</label>
          <input data-testid="stripe-secret-input" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={status?.configured ? "Enter a new key to replace the current one" : "sk_live_..."} autoComplete="off" className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-black bg-white" />
        </div>
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Webhook signing secret (whsec_…)</label>
          <input data-testid="stripe-webhook-input" type="password" value={webhook} onChange={(e) => setWebhook(e.target.value)} placeholder="whsec_..." autoComplete="off" className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-black bg-white" />
        </div>
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Publishable key (optional, pk_…)</label>
          <input data-testid="stripe-publishable-input" value={publishable} onChange={(e) => setPublishable(e.target.value)} placeholder="pk_live_..." autoComplete="off" className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-black bg-white" />
        </div>
        <button onClick={save} disabled={saving} data-testid="stripe-save" className="w-full flex items-center justify-center gap-2 casper-btn rounded-full py-3.5 font-body font-semibold disabled:opacity-60">
          {saving && <Loader2 size={16} className="animate-spin" />} {status?.configured ? "Update Stripe keys" : "Connect Stripe account"}
        </button>
        <div className="text-xs text-zinc-400 leading-relaxed bg-zinc-50 rounded-xl p-3 mt-1">
          Point your Stripe webhook to <span className="font-mono text-zinc-600">{window.location.origin}/api/stripe/webhook</span> and subscribe to <span className="font-mono text-zinc-600">checkout.session.*</span> events. We verify the key with Stripe before saving and never display it back.
        </div>
      </div>
    </div>
  );
}


const CAPTCHA_PROVIDERS = [
  { id: "recaptcha_v2", label: "Google reCAPTCHA v2 (checkbox)" },
  { id: "recaptcha_v3", label: "Google reCAPTCHA v3 (invisible, score)" },
  { id: "turnstile", label: "Cloudflare Turnstile" },
];

function CaptchaSettings() {
  const [status, setStatus] = useState(null);
  const [active, setActive] = useState("none");
  const [minScore, setMinScore] = useState(0.5);
  const [keys, setKeys] = useState({
    recaptcha_v2: { site_key: "", secret_key: "" },
    recaptcha_v3: { site_key: "", secret_key: "" },
    turnstile: { site_key: "", secret_key: "" },
  });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/captcha-settings").then((r) => {
    setStatus(r.data);
    setActive(r.data.active_provider || "none");
    setMinScore(r.data.min_score ?? 0.5);
    const p = r.data.providers || {};
    setKeys({
      recaptcha_v2: { site_key: p.recaptcha_v2?.site_key || "", secret_key: "" },
      recaptcha_v3: { site_key: p.recaptcha_v3?.site_key || "", secret_key: "" },
      turnstile: { site_key: p.turnstile?.site_key || "", secret_key: "" },
    });
  });
  useEffect(() => { load(); }, []);

  const setKey = (prov, field, val) => setKeys((s) => ({ ...s, [prov]: { ...s[prov], [field]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/captcha-settings", { active_provider: active, min_score: parseFloat(minScore) || 0.5, providers: keys });
      toast.success("Bot-protection settings saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save settings"); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-black bg-white";

  return (
    <div className="bg-white border border-zinc-200/80 rounded-2xl p-6 mt-6" data-testid="captcha-settings">
      <div className="flex items-center gap-3 mb-5 pb-5 border-b border-zinc-100">
        <div className="h-10 w-10 rounded-full bg-[#ccff00] text-black flex items-center justify-center"><Bot size={18} /></div>
        <div className="flex-1">
          <div className="font-heading font-semibold">Signup protection — CAPTCHA</div>
          <div className="text-xs text-zinc-500">Protect the merchant signup form with reCAPTCHA or Cloudflare Turnstile.</div>
        </div>
      </div>

      <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Active provider</label>
      <select data-testid="captcha-provider-select" value={active} onChange={(e) => setActive(e.target.value)} className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-black bg-white mb-4">
        <option value="none">Disabled</option>
        {CAPTCHA_PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
      </select>

      {active !== "none" && !status?.providers?.[active]?.secret_set && (!keys[active]?.secret_key || keys[active]?.secret_key === "__CLEAR__") && (
        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4" data-testid="captcha-no-secret-warning">
          <span>⚠️ No secret key set for the active provider — CAPTCHA will <strong>not</strong> be enforced on the signup form until you add and save the secret key below.</span>
        </div>
      )}

      {active === "recaptcha_v3" && (
        <div className="mb-4">
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Minimum score (0–1, reject below)</label>
          <input data-testid="captcha-minscore" type="number" step="0.1" min="0" max="1" value={minScore} onChange={(e) => setMinScore(e.target.value)} className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-black bg-white" />
        </div>
      )}

      <div className="space-y-4">
        {CAPTCHA_PROVIDERS.map((p) => (
          <div key={p.id} className={`rounded-xl p-4 border ${active === p.id ? "border-black bg-zinc-50/60" : "border-zinc-200"}`} data-testid={`captcha-block-${p.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-heading font-semibold">{p.label}</div>
              {status?.providers?.[p.id]?.secret_set && (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 size={13} /> secret saved</span>
                  <button type="button" onClick={() => setKey(p.id, "secret_key", "__CLEAR__")} data-testid={`captcha-${p.id}-clear`} className="text-xs text-zinc-400 hover:text-rose-500">Clear</button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <input data-testid={`captcha-${p.id}-site`} value={keys[p.id].site_key} onChange={(e) => setKey(p.id, "site_key", e.target.value)} placeholder="Site key (public)" className={inputCls} />
              <input data-testid={`captcha-${p.id}-secret`} type="password" value={keys[p.id].secret_key} onChange={(e) => setKey(p.id, "secret_key", e.target.value)} autoComplete="off"
                placeholder={status?.providers?.[p.id]?.secret_set ? "Secret saved — enter to replace" : "Secret key"} className={inputCls} />
              {keys[p.id].secret_key === "__CLEAR__" && <p className="text-xs text-rose-500">Secret will be removed on save.</p>}
            </div>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving} data-testid="captcha-save" className="w-full flex items-center justify-center gap-2 casper-btn rounded-full py-3.5 font-body font-semibold disabled:opacity-60 mt-5">
        {saving && <Loader2 size={16} className="animate-spin" />} Save bot-protection
      </button>
      <p className="text-xs text-zinc-400 leading-relaxed bg-zinc-50 rounded-xl p-3 mt-3">
        Secret keys are stored server-side and never shown again. The public signup form uses only the site key. Leave provider "Disabled" to skip CAPTCHA.
      </p>
    </div>
  );
}

function GeoSettings() {
  const [status, setStatus] = useState(null);
  const [active, setActive] = useState("ipstack");
  const [ipstack, setIpstack] = useState("");
  const [ipinfo, setIpinfo] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/geo-settings").then((r) => {
    setStatus(r.data);
    setActive(r.data.active_provider || "ipstack");
    setIpstack(""); setIpinfo("");
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/geo-settings", { active_provider: active, ipstack_key: ipstack, ipinfo_token: ipinfo });
      toast.success("Geolocation settings saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save settings"); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-black bg-white";

  return (
    <div className="bg-white border border-zinc-200/80 rounded-2xl p-6 mt-6" data-testid="geo-settings">
      <div className="flex items-center gap-3 mb-5 pb-5 border-b border-zinc-100">
        <div className="h-10 w-10 rounded-full bg-[#ccff00] text-black flex items-center justify-center"><Globe size={18} /></div>
        <div className="flex-1">
          <div className="font-heading font-semibold">IP geolocation</div>
          <div className="text-xs text-zinc-500">Geolocate new leads (country / city / flag), flag country mismatches, and power the Analytics map.</div>
        </div>
      </div>

      <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Primary provider (the other is used as fallback)</label>
      <select data-testid="geo-provider-select" value={active} onChange={(e) => setActive(e.target.value)} className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-black bg-white mb-4">
        <option value="ipstack">ipstack (primary)</option>
        <option value="ipinfo">ipinfo (primary)</option>
      </select>

      <div className="space-y-4">
        <div className="rounded-xl p-4 border border-zinc-200">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-heading font-semibold">ipstack access key</div>
            {status?.ipstack_set && <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 size={13} /> key saved</span>}
          </div>
          <input data-testid="geo-ipstack-key" type="password" value={ipstack} onChange={(e) => setIpstack(e.target.value)} autoComplete="off"
            placeholder={status?.ipstack_set ? "Saved — enter to replace" : "ipstack access key"} className={inputCls} />
        </div>
        <div className="rounded-xl p-4 border border-zinc-200">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-heading font-semibold">ipinfo access token</div>
            {status?.ipinfo_set && <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 size={13} /> token saved</span>}
          </div>
          <input data-testid="geo-ipinfo-token" type="password" value={ipinfo} onChange={(e) => setIpinfo(e.target.value)} autoComplete="off"
            placeholder={status?.ipinfo_set ? "Saved — enter to replace" : "ipinfo access token"} className={inputCls} />
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="geo-save" className="w-full flex items-center justify-center gap-2 casper-btn rounded-full py-3.5 font-body font-semibold disabled:opacity-60 mt-5">
        {saving && <Loader2 size={16} className="animate-spin" />} Save geolocation
      </button>
    </div>
  );
}


function InstagramSettings() {
  const [status, setStatus] = useState(null);
  const [appId, setAppId] = useState("");
  const [secret, setSecret] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [connections, setConnections] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/instagram-settings").then((r) => {
    setStatus(r.data);
    setAppId(r.data.app_id || "");
    setEnabled(!!r.data.enabled);
    setSecret("");
  });
  const loadConns = () => api.get("/admin/instagram/connections").then((r) => setConnections(r.data || [])).catch(() => {});
  useEffect(() => { load(); loadConns(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/instagram-settings", { app_id: appId.trim(), app_secret: secret.trim(), enabled });
      toast.success("Instagram settings saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save Instagram settings"); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-black bg-white";

  return (
    <div className="bg-white border border-zinc-200/80 rounded-2xl p-6 mt-6" data-testid="instagram-settings">
      <div className="flex items-center gap-3 mb-5 pb-5 border-b border-zinc-100">
        <div className="h-10 w-10 rounded-full text-white flex items-center justify-center" style={{ background: "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)" }}><Instagram size={18} /></div>
        <div className="flex-1">
          <div className="font-heading font-semibold">Instagram login — auto-build storefronts</div>
          <div className="text-xs text-zinc-500">Let merchants "Continue with Instagram" to auto-create a draft store from their profile.</div>
        </div>
      </div>

      <label className="flex items-center gap-3 mb-4 cursor-pointer">
        <input type="checkbox" data-testid="ig-enabled-toggle" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 accent-black" />
        <span className="text-sm font-body">Show "Continue with Instagram" on the merchant signup form</span>
      </label>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Meta App ID</label>
          <input data-testid="ig-app-id" value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="e.g. 1234567890123456" className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Meta App Secret {status?.secret_set && <span className="text-emerald-600 normal-case">· saved</span>}</label>
          <input data-testid="ig-app-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="off"
            placeholder={status?.secret_set ? "Saved — enter to replace" : "App secret"} className={inputCls} />
        </div>
        <button onClick={save} disabled={saving} data-testid="ig-save" className="w-full flex items-center justify-center gap-2 casper-btn rounded-full py-3.5 font-body font-semibold disabled:opacity-60">
          {saving && <Loader2 size={16} className="animate-spin" />} Save Instagram settings
        </button>
        <div className="text-xs text-zinc-400 leading-relaxed bg-zinc-50 rounded-xl p-3 mt-1">
          In your <span className="font-semibold text-zinc-600">Meta Developer App → Instagram → Business login</span>, add this exact redirect URI:
          <div className="font-mono text-zinc-700 mt-1 break-all" data-testid="ig-redirect-uri">{status?.redirect_uri || `${window.location.origin}/api/auth/instagram/callback`}</div>
          Requires an Instagram <span className="font-semibold">professional</span> account and the <span className="font-mono">instagram_business_basic</span> permission. The app secret is stored server-side and never shown again.
        </div>
      </div>

      {connections.length > 0 && (
        <div className="mt-5" data-testid="ig-connections">
          <div className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Connected accounts ({connections.length})</div>
          <div className="space-y-2">
            {connections.map((c) => (
              <div key={c.ig_user_id} className="flex items-center justify-between text-sm bg-zinc-50 rounded-xl px-4 py-2.5">
                <span className="font-semibold">@{c.username}</span>
                <span className="text-zinc-500 font-mono text-xs">/{c.store_handle}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UpsSettings() {
  const [status, setStatus] = useState(null);
  const [clientId, setClientId] = useState("");
  const [secret, setSecret] = useState("");
  const [shipper, setShipper] = useState("");
  const [env, setEnv] = useState("cie");
  const [enabled, setEnabled] = useState(false);
  const [origin, setOrigin] = useState({ name: "", phone: "", line1: "", city: "", state: "", postal: "", country: "NL" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/ups-settings").then((r) => {
    setStatus(r.data);
    setClientId(r.data.client_id || "");
    setShipper(r.data.shipper_number || "");
    setEnv(r.data.environment || "cie");
    setEnabled(!!r.data.enabled);
    setOrigin({ name: "", phone: "", line1: "", city: "", state: "", postal: "", country: "NL", ...(r.data.origin || {}) });
    setSecret("");
  });
  useEffect(() => { load(); }, []);

  const setO = (k) => (e) => setOrigin((o) => ({ ...o, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/ups-settings", {
        client_id: clientId.trim(), client_secret: secret.trim(), shipper_number: shipper.trim(),
        environment: env, enabled, origin,
      });
      toast.success("UPS settings saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save UPS settings"); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-black bg-white";
  const smallCls = "border border-zinc-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-black bg-white";

  return (
    <div className="bg-white border border-zinc-200/80 rounded-2xl p-6 mt-6" data-testid="ups-settings">
      <div className="flex items-center gap-3 mb-5 pb-5 border-b border-zinc-100">
        <div className="h-10 w-10 rounded-full bg-[#351c15] text-[#ffb500] flex items-center justify-center"><Package size={18} /></div>
        <div className="flex-1">
          <div className="font-heading font-semibold">Shipping — UPS</div>
          <div className="text-xs text-zinc-500">Create UPS shipments & download labels straight from an order, alongside Sendy.</div>
        </div>
      </div>

      <label className="flex items-center gap-3 mb-4 cursor-pointer">
        <input type="checkbox" data-testid="ups-enabled-toggle" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 accent-black" />
        <span className="text-sm font-body">Show the "Send To UPS" button on paid orders</span>
      </label>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="col-span-2">
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">UPS Client ID</label>
          <input data-testid="ups-client-id" value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls} />
        </div>
        <div className="col-span-2">
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">UPS Client Secret {status?.secret_set && <span className="text-emerald-600 normal-case">· saved</span>}</label>
          <input data-testid="ups-client-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="off"
            placeholder={status?.secret_set ? "Saved — enter to replace" : "Client secret"} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Shipper Account No. (6-digit)</label>
          <input data-testid="ups-shipper" value={shipper} onChange={(e) => setShipper(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Environment</label>
          <select data-testid="ups-environment" value={env} onChange={(e) => setEnv(e.target.value)} className={inputCls}>
            <option value="cie">Sandbox (CIE)</option>
            <option value="production">Production</option>
          </select>
        </div>
      </div>

      <div className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 mt-4 mb-2">Ship-from (origin) address</div>
      <div className="grid grid-cols-2 gap-2.5">
        <input data-testid="ups-origin-name" className={smallCls} placeholder="Name / company" value={origin.name} onChange={setO("name")} />
        <input data-testid="ups-origin-phone" className={smallCls} placeholder="Phone" value={origin.phone} onChange={setO("phone")} />
        <input data-testid="ups-origin-line1" className={`${smallCls} col-span-2`} placeholder="Street + number" value={origin.line1} onChange={setO("line1")} />
        <input data-testid="ups-origin-city" className={smallCls} placeholder="City" value={origin.city} onChange={setO("city")} />
        <input data-testid="ups-origin-state" className={smallCls} placeholder="State/Province (US/CA/IE)" value={origin.state} onChange={setO("state")} />
        <input data-testid="ups-origin-postal" className={smallCls} placeholder="Postal code" value={origin.postal} onChange={setO("postal")} />
        <input data-testid="ups-origin-country" className={smallCls} placeholder="Country (NL)" value={origin.country} onChange={setO("country")} />
      </div>

      <button onClick={save} disabled={saving} data-testid="ups-save" className="w-full flex items-center justify-center gap-2 casper-btn rounded-full py-3.5 font-body font-semibold disabled:opacity-60 mt-5">
        {saving && <Loader2 size={16} className="animate-spin" />} Save UPS settings
      </button>
      <p className="text-xs text-zinc-400 leading-relaxed bg-zinc-50 rounded-xl p-3 mt-3">
        Get your Client ID/Secret from the <span className="font-semibold text-zinc-600">UPS Developer Portal</span> app. Use Sandbox (CIE) to test — real labels/charges only happen in Production. The secret is stored server-side and never shown again.
      </p>
    </div>
  );
}
function MailgunSettings() {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [domain, setDomain] = useState("");
  const [region, setRegion] = useState("us");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [webhookKey, setWebhookKey] = useState("");
  const [suppressed, setSuppressed] = useState(0);
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/admin/mailgun-settings").then((r) => {
    setStatus(r.data);
    setDomain(r.data.domain || "");
    setRegion(r.data.region || "us");
    setFromEmail(r.data.from_email || "");
    setFromName(r.data.from_name || "");
    setEnabled(!!r.data.enabled);
    setApiKey(""); setWebhookKey("");
  });
  const loadSuppressed = () => api.get("/admin/mailgun/suppressions").then((r) => setSuppressed(r.data?.count || 0)).catch(() => {});
  useEffect(() => { load(); loadSuppressed(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/mailgun-settings", {
        api_key: apiKey.trim(), domain: domain.trim(), region,
        from_email: fromEmail.trim(), from_name: fromName.trim(),
        webhook_signing_key: webhookKey.trim(), enabled,
      });
      toast.success("Mailgun settings saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save Mailgun settings"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter a test recipient email"); return; }
    setTesting(true);
    try {
      await api.post("/admin/mailgun/test", { to: testTo.trim() });
      toast.success("Test email sent — check that inbox.");
    } catch (e) { toast.error(e.response?.data?.detail || "Test send failed"); }
    finally { setTesting(false); }
  };

  const inputCls = "w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-black bg-white";

  return (
    <div className="bg-white border border-zinc-200/80 rounded-2xl p-6 mt-6" data-testid="mailgun-settings">
      <div className="flex items-center gap-3 mb-5 pb-5 border-b border-zinc-100">
        <div className="h-10 w-10 rounded-full bg-[#c02428] text-white flex items-center justify-center"><Mail size={18} /></div>
        <div className="flex-1">
          <div className="font-heading font-semibold">Email — Mailgun</div>
          <div className="text-xs text-zinc-500">Send all StoreIG emails (order confirmations, leads, newsletter, campaigns) through your own Mailgun account.</div>
        </div>
      </div>

      <label className="flex items-center gap-3 mb-4 cursor-pointer">
        <input type="checkbox" data-testid="mailgun-enabled-toggle" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 accent-black" />
        <span className="text-sm font-body">Route all outgoing email through Mailgun {!enabled && <span className="text-zinc-400">(off = platform default)</span>}</span>
      </label>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Private API key {status?.api_key_set && <span className="text-emerald-600 normal-case">· saved</span>}</label>
          <input data-testid="mailgun-api-key" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} autoComplete="off"
            placeholder={status?.api_key_set ? "Saved — enter to replace" : "key-…"} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Sending domain</label>
          <input data-testid="mailgun-domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="mg.yourdomain.com" className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Region</label>
          <select data-testid="mailgun-region" value={region} onChange={(e) => setRegion(e.target.value)} className={inputCls}>
            <option value="us">US (api.mailgun.net)</option>
            <option value="eu">EU (api.eu.mailgun.net)</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">From email</label>
          <input data-testid="mailgun-from-email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder={domain ? `postmaster@${domain}` : "you@yourdomain.com"} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">From name</label>
          <input data-testid="mailgun-from-name" value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="StoreIG" className={inputCls} />
        </div>
        <div className="col-span-2">
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Webhook signing key {status?.webhook_key_set && <span className="text-emerald-600 normal-case">· saved</span>}</label>
          <input data-testid="mailgun-webhook-key" type="password" value={webhookKey} onChange={(e) => setWebhookKey(e.target.value)} autoComplete="off"
            placeholder={status?.webhook_key_set ? "Saved — enter to replace" : "HTTP webhook signing key"} className={inputCls} />
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="mailgun-save" className="w-full flex items-center justify-center gap-2 casper-btn rounded-full py-3.5 font-body font-semibold disabled:opacity-60 mt-5">
        {saving && <Loader2 size={16} className="animate-spin" />} Save Mailgun settings
      </button>

      <div className="flex gap-2 mt-3">
        <input data-testid="mailgun-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="flex-1 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-black bg-white" />
        <button onClick={sendTest} disabled={testing} data-testid="mailgun-test-send" className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-60 whitespace-nowrap">
          {testing ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Send test
        </button>
      </div>
      <p className="text-xs text-zinc-400 leading-relaxed bg-zinc-50 rounded-xl p-3 mt-3">
        Get your <span className="font-semibold text-zinc-600">Private API key</span> from Mailgun → API Keys, and your verified <span className="font-semibold text-zinc-600">sending domain</span> from Sending → Domains. EU accounts must pick the EU region. The API key is stored server-side and never shown again.
      </p>
      <div className="text-xs text-zinc-500 leading-relaxed bg-zinc-50 rounded-xl p-3 mt-3" data-testid="mailgun-webhook-info">
        <div className="font-heading font-bold uppercase tracking-[0.12em] text-zinc-500 mb-1">Auto-clean webhook</div>
        In Mailgun → <span className="font-semibold">Webhooks</span>, add this URL for the <span className="font-semibold">Permanent Failure</span>, <span className="font-semibold">Complained</span> and <span className="font-semibold">Unsubscribed</span> events, then paste the HTTP webhook signing key above:
        <div className="font-mono text-zinc-700 mt-1 break-all" data-testid="mailgun-webhook-url">{window.location.origin}/api/mailgun/webhook</div>
        <div className="mt-2 inline-flex items-center gap-1.5 text-emerald-700 font-semibold" data-testid="mailgun-suppressed-count">
          <CheckCircle2 size={13} /> {suppressed} address{suppressed === 1 ? "" : "es"} auto-cleaned so far
        </div>
      </div>
    </div>
  );
}

function SecurityAlerts() {
  const [data, setData] = useState({ unread: 0, alerts: [] });
  const [locked, setLocked] = useState({ locked: [], blocked_ips: [] });
  const load = () => api.get("/admin/security/alerts").then((r) => setData(r.data || { unread: 0, alerts: [] })).catch(() => {});
  const loadLocked = () => api.get("/admin/security/locked-ips").then((r) => setLocked(r.data || { locked: [], blocked_ips: [] })).catch(() => {});
  useEffect(() => { load(); loadLocked(); }, []);

  const markRead = async () => {
    try { await api.post("/admin/security/alerts/read"); await load(); } catch (e) { console.error("Failed to mark security alerts read:", e); }
  };
  const whitelist = async (row) => {
    try { await api.post("/admin/security/whitelist", { store_id: row.store_id, ip: row.ip }); toast.success(`Whitelisted ${row.ip}`); loadLocked(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not whitelist"); }
  };
  const block = async (ip) => {
    try { await api.post("/admin/security/block", { ip }); toast.success(`Blocked ${ip}`); loadLocked(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not block"); }
  };
  const unblock = async (ip) => {
    try { await api.post("/admin/security/unblock", { ip }); toast.success(`Unblocked ${ip}`); loadLocked(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not unblock"); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="bg-white border border-zinc-200/80 rounded-2xl p-6 mb-6" data-testid="security-alerts">
      <div className="flex items-center gap-3 mb-5 pb-5 border-b border-zinc-100">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${data.unread ? "bg-rose-600 text-white" : "bg-zinc-100 text-zinc-500"}`}><AlertTriangle size={18} /></div>
        <div className="flex-1">
          <div className="font-heading font-semibold">Security — brute-force alerts</div>
          <div className="text-xs text-zinc-500">Failed access attempts on members-only stores. Progressive lockout auto-applies per IP.</div>
        </div>
        {data.unread > 0 && (
          <button onClick={markRead} data-testid="security-mark-read" className="text-xs font-semibold border border-zinc-300 rounded-full px-3 py-1.5 hover:bg-zinc-50">
            Mark all read ({data.unread})
          </button>
        )}
      </div>
      {data.alerts.length === 0 ? (
        <p className="text-sm text-zinc-400" data-testid="security-empty">No suspicious activity. All members-only stores are quiet. 🔒</p>
      ) : (
        <div className="space-y-2 max-h-72 overflow-auto">
          {data.alerts.map((a) => (
            <div key={a.id} className={`flex items-center justify-between text-sm rounded-xl px-4 py-2.5 ${a.read ? "bg-zinc-50" : "bg-rose-50"}`} data-testid={`security-alert-${a.id}`}>
              <div>
                <span className="font-semibold">{a.store_name || a.handle}</span>
                <span className="text-zinc-500"> · {a.fails} fails from </span>
                <span className="font-mono text-xs">{a.ip}</span>
              </div>
              <span className="text-[11px] text-zinc-400 whitespace-nowrap ml-3">{fmt(a.created_at)}</span>
            </div>
          ))}
        </div>
      )}

      {(locked.locked.length > 0 || locked.blocked_ips.length > 0) && (
        <div className="mt-5 pt-5 border-t border-zinc-100" data-testid="locked-ips-section">
          <div className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Recently locked IPs</div>
          <div className="space-y-2 max-h-72 overflow-auto">
            {locked.locked.map((r) => (
              <div key={r.handle + r.ip} className="flex items-center justify-between gap-2 text-sm bg-zinc-50 rounded-xl px-3 py-2" data-testid={`locked-ip-${r.ip}`}>
                <div className="min-w-0">
                  <span className="font-mono text-xs">{r.ip}</span>
                  <span className="text-zinc-500"> · {r.store_name} · {r.fails} fails</span>
                  {r.locked && <span className="ml-2 text-[11px] text-rose-600 font-semibold">locked {r.locked_minutes}m</span>}
                  {r.whitelisted && <span className="ml-2 text-[11px] text-emerald-600 font-semibold">whitelisted</span>}
                  {r.blocked && <span className="ml-2 text-[11px] text-zinc-800 font-semibold">blocked</span>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!r.whitelisted && r.store_id && (
                    <button onClick={() => whitelist(r)} data-testid={`whitelist-${r.ip}`} className="text-[11px] font-semibold border border-emerald-300 text-emerald-700 rounded-full px-2.5 py-1 hover:bg-emerald-50">Whitelist</button>
                  )}
                  {r.blocked ? (
                    <button onClick={() => unblock(r.ip)} data-testid={`unblock-${r.ip}`} className="text-[11px] font-semibold border border-zinc-300 rounded-full px-2.5 py-1 hover:bg-zinc-100">Unblock</button>
                  ) : (
                    <button onClick={() => block(r.ip)} data-testid={`block-${r.ip}`} className="text-[11px] font-semibold border border-rose-300 text-rose-600 rounded-full px-2.5 py-1 hover:bg-rose-50">Block</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {locked.blocked_ips.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Blocked IPs ({locked.blocked_ips.length})</div>
              <div className="flex flex-wrap gap-2">
                {locked.blocked_ips.map((ip) => (
                  <span key={ip} className="inline-flex items-center gap-1.5 text-xs font-mono bg-zinc-900 text-white rounded-full px-2.5 py-1" data-testid={`blocked-chip-${ip}`}>
                    {ip}
                    <button onClick={() => unblock(ip)} data-testid={`chip-unblock-${ip}`} className="text-white/70 hover:text-white">×</button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AnalyticsSettings() {
  const [pid, setPid] = useState("");
  const [gaId, setGaId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/public/analytics").then((r) => {
    setPid(r.data?.swetrix_pid || "");
    setGaId(r.data?.ga_id || "");
    setEnabled(r.data?.enabled !== false);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/analytics", { swetrix_pid: pid.trim(), ga_id: gaId.trim(), enabled });
      toast.success("Analytics settings saved — reload public pages to apply.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save analytics settings"); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-black bg-white";

  return (
    <div className="bg-white border border-zinc-200/80 rounded-2xl p-6 mb-6" data-testid="analytics-settings">
      <div className="flex items-center gap-3 mb-5 pb-5 border-b border-zinc-100">
        <div className="h-10 w-10 rounded-full bg-indigo-600 text-white flex items-center justify-center"><Activity size={18} /></div>
        <div className="flex-1">
          <div className="font-heading font-semibold">Web analytics — Swetrix &amp; Google Analytics</div>
          <div className="text-xs text-zinc-500">Tracks visitors on public pages only. The admin dashboard is excluded automatically.</div>
        </div>
      </div>

      <label className="flex items-center gap-3 mb-4 cursor-pointer">
        <input type="checkbox" data-testid="analytics-enabled-toggle" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 accent-black" />
        <span className="text-sm font-body">Enable analytics on public pages</span>
      </label>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Swetrix Project ID</label>
          <input data-testid="analytics-swetrix-pid" value={pid} onChange={(e) => setPid(e.target.value)} placeholder="xxxxxxxx-xxxx-..." className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Google Analytics 4 Measurement ID</label>
          <input data-testid="analytics-ga-id" value={gaId} onChange={(e) => setGaId(e.target.value)} placeholder="G-XXXXXXXXXX" className={inputCls} />
          <div className="text-[11px] text-zinc-400 mt-1">Leave blank to disable Google Analytics. Found in GA → Admin → Data Streams.</div>
        </div>
        <button onClick={save} disabled={saving} data-testid="analytics-save" className="w-full flex items-center justify-center gap-2 casper-btn rounded-full py-3.5 font-body font-semibold disabled:opacity-60">
          {saving && <Loader2 size={16} className="animate-spin" />} Save analytics settings
        </button>
      </div>
    </div>
  );
}

